"use client";

import { useState } from "react";

type Status = "idle" | "sending" | "sent" | "error";

export type NewsletterLabels = {
  placeholder: string;
  submit: string;
  sending: string;
  success: string;
  already: string;
  invalid: string;
  generic: string;
};

export default function NewsletterForm({ labels }: { labels: NewsletterLabels }) {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const email = String(data.get("email") ?? "").trim();

    setStatus("sending");
    setMessage("");

    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, website: String(data.get("website") ?? "") }),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };

      if (!res.ok) {
        setStatus("error");
        setMessage(
          body.error === "already_subscribed"
            ? labels.already
            : body.error === "invalid_email"
              ? labels.invalid
              : labels.generic,
        );
        return;
      }

      form.reset();
      setStatus("sent");
      setMessage(labels.success);
    } catch {
      setStatus("error");
      setMessage(labels.generic);
    }
  }

  const busy = status === "sending";

  return (
    <div className="mt-4">
      <form
        onSubmit={handleSubmit}
        className="flex items-center rounded-full border border-white/20 bg-white/5 p-1.5"
      >
        <label htmlFor="newsletter-email" className="sr-only">
          {labels.placeholder}
        </label>
        <input
          id="newsletter-email"
          name="email"
          type="email"
          required
          autoComplete="email"
          disabled={busy}
          placeholder={labels.placeholder}
          className="w-full bg-transparent px-4 text-sm text-white placeholder:text-white/40 focus:outline-none disabled:opacity-60"
        />
        {/* Champ piège anti-robots */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="hidden"
        />
        <button
          type="submit"
          disabled={busy}
          className="shrink-0 rounded-full bg-brand px-6 py-2.5 text-sm font-medium text-white transition hover:bg-brand-dark disabled:opacity-60"
        >
          {busy ? labels.sending : labels.submit}
        </button>
      </form>

      {message && (
        <p
          aria-live="polite"
          className={`mt-2 text-xs ${status === "sent" ? "text-white/80" : "text-red-300"}`}
        >
          {message}
        </p>
      )}
    </div>
  );
}
